import Konva from "konva";
import { darken, mix, readableColor, saturate, shade } from "polished";
import { type CSSProperties } from "react";

import { SEQ_RESIDUE_AA_COLORS, STRAND_TYPE, VARIANT_TYPE } from "@/settings/settings.ts";

import { type Feature, type Range } from "../../graphql.js";
import { CANVAS_TRACK_MARGIN } from "../../settings/genome-viewer-settings.js";
import { getCssColor } from "../color.js";
import { type Normalize } from "../range.js";
import { variantShapePileupParams } from "./shapes-settings.js";
import {
  type IPileupFeature,
  type IPileupFeatureMapping,
  type IPileupParams,
  type IPileupVariant,
} from "./track-pileup-factory.js";

import ShapeConfig = Konva.ShapeConfig;

/// --- Shapes renderers --- ///

enum ArrowDirection {
  None,
  Left,
  Right,
}

export interface GenomeViewerDefaultShape {
  text?: (text: string, outside?: boolean, fontSize?: number, shouldRender?: (labelWidth) => boolean) => void;
  interactive?: () => GenomeViewerShapeInteractivity;
  highlight?: (...colors: string[]) => void;
}

export interface GenomeViewerShapeInteractivity {
  interactiveShape: Konva.Rect;
  tooltipTargetShape: Konva.Rect;
  doInteract: IFeatureInteraction;
}

export type GenomeViewerShape = GenomeViewerDefaultShape;

interface IShapeRenderedParams {
  layer: Konva.Layer;
  normalize: Normalize;
  interactionLayer?: Konva.Layer;
  visibleRange: Range;
}

export interface IRenderShapeParams {
  start: number;
  stop: number;
  height: number;
  y: number; // from 0
  opacity?: number;
  fill: string;
  hasStroke?: boolean;
  inclusiveStartPosition?: boolean;
}

interface IRenderArrowShapeParams extends IRenderShapeParams {
  direction: ArrowDirection;
}

interface IRenderMappingShapeParams extends IRenderShapeParams {
  shapes: Range[];
}

interface IRenderLineShapeParams extends IRenderShapeParams {
  thickness: number;
}

export interface IRenderMutationShapeParams {
  pileupVariant: IPileupVariant;
  height: number;
  y: number; // from 0
  //fill: string;
  //hasStroke?: boolean;
  count: number;
  showRegion: boolean;
  symbolElementStyle: CSSProperties;
  rangeElementStyle: CSSProperties;
}

type IRenderInsertionsShapeParams = Pick<IRenderShapeParams, "start" | "height" | "y" | "fill">;

function getHighlightFn(
  layer: Konva.Layer,
  start: number,
  stop: number,
  y: number,
  singleBullet = false,
  xPadding = 0,
  yPadding = 0,
): (...colors: string[]) => void {
  return (...colors: string[]): void => {
    const BORDER = 2;
    const RADIUS = 2.5; // Need to be odd because the "+" (pixel perfect in the center)
    const BETWEEN_SPACE = 3;

    // dots, as long as they fit, if not, make the last "grey"
    const shapeWidth = stop - start;
    const quantityFit = singleBullet ? 1 : Math.floor(shapeWidth / (RADIUS * 2 + BETWEEN_SPACE));
    const colorsLength = colors.length;

    // at least a single bullet
    if (colorsLength > 0) {
      for (let i = 0; i < colorsLength; i += 1) {
        const canHaveMore = i < quantityFit - 1 || i === colorsLength - 1;
        const color = canHaveMore ? getCssColor(colors[i]) : "#121314";
        const bgColor = shade(0.5, color);
        // If no indicators fit (@see quantityFit), we still show one, but in the middle of the shape
        const x =
          quantityFit > 0 ? stop - ((i + 1) * (RADIUS * 2) + BETWEEN_SPACE * i) : Math.ceil(stop - shapeWidth / 2);
        const paddedY = y + yPadding;
        const paddedX = x - xPadding;
        const indicatorShape = new Konva.Circle({
          x: paddedX - 0.5,
          y: paddedY - 0.5,
          radius: RADIUS,
          fill: color,
          stroke: bgColor,
          strokeWidth: BORDER,
          fillAfterStrokeEnabled: true,
        });

        layer.add(indicatorShape);
        indicatorShape.moveToTop();

        if (!canHaveMore) {
          // Draw a little "+" to explain that there are more groups/projects that currently shown
          const plusHorizontal = new Konva.Rect({ x: paddedX - 2, y: paddedY - 1, width: 3, height: 1, fill: "white" });
          const plusVertical = new Konva.Rect({ x: paddedX - 1, y: paddedY - 2, width: 1, height: 3, fill: "white" });
          layer.add(plusHorizontal);
          layer.add(plusVertical);
          plusHorizontal.moveToTop();
          plusVertical.moveToTop();
        }

        if (i >= quantityFit - 1) {
          break;
        }
      }
    }
  };
}

interface IRenderVariantConnectorParams {
  pileupVariant: IPileupVariant;
  y: number;
  profileMidpoint: number;
  connectorStyle: CSSProperties;
}

interface IRenderVariantProfileParams {
  pileupVariant: IPileupVariant;
  profileStyle: CSSProperties;
  profileMidpoint: number;
  boxIndex: number;
  symbolY: number;
}

// Each function should return the Konva.Shape as `return { shape: Konva.Shape }` and not directly.
export function getMutationShapeRenderer({ layer, interactionLayer, normalize, visibleRange }: IShapeRenderedParams) {
  const [visibleStart, visibleEnd] = normalize(visibleRange.start - 1, visibleRange.stop, Math.ceil);
  const renderInteraction = getInteractiveRenderer(interactionLayer);

  const renderProfileBox = ({
    pileupVariant,
    profileStyle,
    profileMidpoint,
    symbolY,
    boxIndex,
  }: IRenderVariantProfileParams): GenomeViewerDefaultShape => {
    const crisp = 0.5;
    const regionShape = new Konva.Rect({
      x: Math.floor(profileMidpoint - pileupVariant.profileWidth / 2) + crisp,
      y: symbolY + pileupVariant.profileOffset + variantShapePileupParams.boxHeight * boxIndex - crisp,
      width: pileupVariant.profileWidth,
      height: variantShapePileupParams.boxHeight,
      stroke: profileStyle?.stroke || "#9DA0A6",
      strokeWidth: 1,
      fill: profileStyle?.fill || "#FFF",
    });
    regionShape.transformsEnabled("position");
    layer.add(regionShape);

    function interactive(): GenomeViewerShapeInteractivity {
      // Interactive Shape
      const interactiveShape = new Konva.Rect({
        x: regionShape.x() - crisp,
        y: regionShape.y() - crisp,
        width: regionShape.width() + crisp * 2,
        height: regionShape.height() + crisp * 2,
      });
      interactiveShape.transformsEnabled("position");
      const doHover = renderInteraction(interactiveShape, InteractiveState.Hover);
      layer.add(interactiveShape);
      interactiveShape.on("mouseover", () => doHover.show());
      interactiveShape.on("mouseout", () => doHover.hide());

      // give away full control over interactions
      return { interactiveShape: interactiveShape, tooltipTargetShape: interactiveShape, doInteract: doHover };
    }

    return {
      interactive,
    };
  };

  const renderMutationConnector = ({
    pileupVariant,
    y,
    profileMidpoint,
    connectorStyle,
  }: IRenderVariantConnectorParams) => {
    const symbolWidth: number = variantShapePileupParams.featureHeight - 0.5;
    const halfLanePadding: number = variantShapePileupParams.lanePadding / 2;
    const halfSymbolWidth: number = Math.round(symbolWidth / 2);

    const crisp = 0.5;
    const c1: number[] = [pileupVariant.midpoint + crisp, y + halfSymbolWidth];
    const c2: number[] = [profileMidpoint + crisp, y + symbolWidth + halfLanePadding];
    const c3: number[] = [profileMidpoint + crisp, y + pileupVariant.profileOffset];
    const points = [...c1, ...c2, ...c3];
    const connectorShape = new Konva.Line({
      points,
      strokeEnabled: true,
      stroke: connectorStyle?.stroke || "#D8D8D8",
      strokeWidth: 1,
      listening: false,
    });
    connectorShape.transformsEnabled("position");
    layer.add(connectorShape);
  };

  const renderMutationShape = ({
    pileupVariant,
    y,
    height,
    count,
    showRegion,
    symbolElementStyle,
    rangeElementStyle,
  }: IRenderMutationShapeParams): GenomeViewerDefaultShape => {
    const STROKE_WIDTH = 1;
    //let [begin, end, width] = normalize(midPoint - 1, midPoint - 1, Math.ceil);
    const variantType: string = pileupVariant.variants[0].variantType.toLowerCase();
    const symbolWidth: number = variantShapePileupParams.featureHeight - 0.5;
    const halfSymbolWidth: number = Math.round(symbolWidth / 2);
    const fifthSymbolWidth: number = Math.round(symbolWidth / 5);

    const begin: number = showRegion ? pileupVariant.x : pileupVariant.midpoint;
    const symbolBegin: number = pileupVariant.midpoint - halfSymbolWidth;
    const end: number = showRegion ? pileupVariant.x + pileupVariant.width : pileupVariant.midpoint;
    const symbolEnd: number = pileupVariant.midpoint + halfSymbolWidth;

    // prepare label inside the symbol
    let textShape = null;
    let textWidth: number = null;
    if (count > 1) {
      textShape = new Konva.Text({
        fontSize: count > 9 ? 9 : 10,
        fontFamily: "Roboto Flex, sans-serif",
        fontStyle: "bold",
        fill: "white",
      });

      textShape.text(String(count));
      textWidth = textShape.width();
      const textHeight = textShape.height();
      textShape.x(pileupVariant.midpoint - textWidth / 2);
      const textOffset: number =
        variantType === VARIANT_TYPE.DELETION ? 2 : variantType === VARIANT_TYPE.INSERTION ? -2 : 0;
      textShape.y(y + symbolWidth / 2 - textHeight / 2 + textOffset);
      textShape.transformsEnabled("position");
      layer.add(textShape);
    }

    if (showRegion && pileupVariant.width > symbolWidth) {
      const regionShape = new Konva.Rect({
        x: pileupVariant.x,
        y: y - STROKE_WIDTH,
        width: pileupVariant.width,
        height: symbolWidth + STROKE_WIDTH + 0.5,
        stroke: rangeElementStyle.stroke,
        fill: rangeElementStyle.fill,
      });
      regionShape.transformsEnabled("position");
      layer.add(regionShape);
    }

    const enlarge: number = Math.min(count - 1, 2);
    const shape = getMutationShape();

    function getMutationShape() {
      let points = [];
      switch (variantType) {
        case VARIANT_TYPE.POINT:
          const pnt1: number[] = [pileupVariant.midpoint - halfSymbolWidth - enlarge, y + halfSymbolWidth];
          const pnt2: number[] = [pileupVariant.midpoint, y - enlarge];
          const pnt3: number[] = [pileupVariant.midpoint + halfSymbolWidth + enlarge, y + halfSymbolWidth];
          const pnt4: number[] = [pileupVariant.midpoint, y + symbolWidth + enlarge];
          points = [...pnt1, ...pnt2, ...pnt3, ...pnt4];
          return getLineBasedShape(points);
          break;
        case VARIANT_TYPE.DELETION:
          const del1: number[] = [pileupVariant.midpoint, y - enlarge];
          const del2: number[] = [pileupVariant.midpoint + halfSymbolWidth + enlarge, y + symbolWidth + enlarge];
          const del3: number[] = [pileupVariant.midpoint - halfSymbolWidth - enlarge, y + symbolWidth + enlarge];
          points = [...del1, ...del2, ...del3];
          return getLineBasedShape(points);
          break;
        case VARIANT_TYPE.INSERTION:
          const ins1: number[] = [pileupVariant.midpoint - halfSymbolWidth, y - enlarge];
          const ins2: number[] = [pileupVariant.midpoint + halfSymbolWidth + enlarge, y - enlarge];
          const ins3: number[] = [pileupVariant.midpoint, y + symbolWidth + enlarge];
          points = [...ins1, ...ins2, ...ins3];
          return getLineBasedShape(points);
          break;
        case VARIANT_TYPE.INDEL:
          const ind1: number[] = [pileupVariant.midpoint - halfSymbolWidth - enlarge, y - enlarge];
          const ind2: number[] = [pileupVariant.midpoint + halfSymbolWidth + enlarge, y - enlarge];
          const ind3: number[] = [pileupVariant.midpoint + fifthSymbolWidth + enlarge, y + halfSymbolWidth];
          const ind4: number[] = [pileupVariant.midpoint + halfSymbolWidth + enlarge, y + symbolWidth + enlarge];
          const ind5: number[] = [pileupVariant.midpoint - halfSymbolWidth - enlarge, y + symbolWidth + enlarge];
          const ind6: number[] = [pileupVariant.midpoint - fifthSymbolWidth - enlarge, y + halfSymbolWidth];
          points = [...ind1, ...ind2, ...ind3, ...ind4, ...ind5, ...ind6];
          return getLineBasedShape(points);
          break;
        case VARIANT_TYPE.TRANSLOCATION_DELETION:
        case VARIANT_TYPE.TRANSLOCATION_INSERTION:
          return getCircleShape();
      }

      // Shape based on point coordinates
      function getLineBasedShape(points) {
        return new Konva.Line({
          points,
          fill: symbolElementStyle.fill,
          opacity: 1,
          closed: true,
          strokeEnabled: true,
          stroke: symbolElementStyle.stroke,
          strokeWidth: STROKE_WIDTH,
          fillAfterStrokeEnabled: false,
        });
      }

      // Shape based on point coordinates
      function getCircleShape() {
        return new Konva.Circle({
          x: pileupVariant.midpoint,
          y: y + halfSymbolWidth,
          radius: halfSymbolWidth - 1,
          fill: symbolElementStyle.fill,
          opacity: 1,
          strokeEnabled: true,
          stroke: symbolElementStyle.stroke,
          strokeWidth: STROKE_WIDTH,
          fillAfterStrokeEnabled: false,
        });
      }
    }

    shape.transformsEnabled("position");
    layer.add(shape);

    if (textShape) {
      // need the shape to be drawn on top
      textShape.moveToTop();
    }

    const highlight = getHighlightFn(layer, symbolBegin, symbolEnd, y, true, -2, 2);

    function interactive(): GenomeViewerShapeInteractivity {
      const interactionOverlay = getMutationShape();
      // Interactive Shape
      const tooltipStart = Math.max(Math.min(begin, symbolBegin), visibleStart);
      const tooltipStop = Math.min(Math.max(end, symbolEnd), visibleEnd);
      const interactiveShape = new Konva.Rect({ x: tooltipStart, y, width: tooltipStop - tooltipStart, height });
      interactiveShape.transformsEnabled("position");
      const doHover = renderInteraction(interactionOverlay, InteractiveState.Hover);

      layer.add(interactiveShape);
      interactiveShape.on("mouseover", () => doHover.show());
      interactiveShape.on("mouseout", () => doHover.hide());

      // give away full control over interactions
      return { interactiveShape: interactiveShape, tooltipTargetShape: interactiveShape, doInteract: doHover };
    }

    return {
      interactive,
      highlight,
    };
  };

  return { renderMutationShape, renderMutationConnector, renderProfileBox };
}

// Each function should return the Konva.Shape as `return { shape: Konva.Shape }` and not directly.
function getShapeRenderer({ layer, interactionLayer, normalize, visibleRange }: IShapeRenderedParams) {
  // This will be used to render the hover effect
  const renderInteraction = getInteractiveRenderer(interactionLayer);
  const [visibleStart, visibleEnd] = normalize(visibleRange.start - 1, visibleRange.stop, Math.ceil);

  const renderMapping = ({
    start,
    stop,
    shapes,
    fill,
    y,
    height,
    hasStroke,
    inclusiveStartPosition,
  }: IRenderMappingShapeParams): GenomeViewerDefaultShape => {
    const [begin, end, width] = normalize(inclusiveStartPosition ? start - 1 : start, stop, Math.ceil);
    const stroke = darken(0.45, fill);
    //const textHeight = showLabel ? 12 : 0;
    //const shapeOffsetY = (row + 1) * -textHeight;

    // show the mapping shapes as one single consolidated shape if the total width is very small (rather than displaying every single mapping shape using pixel wide connected shapes)
    if (shapes.length > 1 && width < 15) {
      renderArrow({
        start: start,
        stop: stop,
        direction: ArrowDirection.None,
        fill,
        height,
        y,
        hasStroke,
        inclusiveStartPosition,
      });
    } else {
      shapes.forEach((mapping, index) => {
        // Already normalize, so we need range values
        renderArrow({
          start: mapping.start,
          stop: mapping.stop,
          direction: ArrowDirection.None,
          fill,
          height,
          y,
          hasStroke,
          inclusiveStartPosition,
        });

        if (index > 0) {
          const previousMapping: { start; stop } = shapes[index - 1];
          const [begin] = normalize(
            inclusiveStartPosition ? mapping.start - 1 : mapping.start,
            mapping.stop,
            Math.ceil,
          );
          const [, previousEnd] = normalize(previousMapping.start, previousMapping.stop, Math.ceil);

          // Don't draw connector unless there is sufficient space between mapping elements
          if (begin - previousEnd > 0) {
            const connectorXMiddle = (previousEnd + begin) / 2;
            const shapeMiddle = Math.ceil(y + height / 2) + 0.5;

            let points: number[] = [begin, shapeMiddle, connectorXMiddle, y + 0.5, previousEnd, shapeMiddle];

            if (begin - previousEnd < 3) {
              // Just draw a strait line instead of a "^" shape
              const halfMiddle = y + Math.ceil((shapeMiddle - y) / 2);
              points = [begin, halfMiddle, previousEnd, halfMiddle];
            }

            const shapeConnector = new Konva.Line({
              points,
              fill,
              stroke,
              closed: false,
              listening: false,
              strokeWidth: 1,
            });
            shapeConnector.transformsEnabled("position");
            layer.add(shapeConnector);
          }
        }
      });
    }

    const highlight = getHighlightFn(layer, Math.max(begin, visibleStart), Math.min(end, visibleEnd), y);

    let textInteractiveShape: Konva.Rect;
    function interactive(): GenomeViewerShapeInteractivity {
      // Interactive Shape
      const interactiveShapeStart = Math.max(begin, visibleStart);
      const interactiveShapeStop = Math.min(end, visibleEnd);
      const interactiveShape = new Konva.Rect({
        x: interactiveShapeStart,
        y,
        width: interactiveShapeStop - interactiveShapeStart,
        height,
      });
      interactiveShape.transformsEnabled("position");
      const doHover = renderInteraction(interactiveShape, InteractiveState.Hover);

      const layerShape = textInteractiveShape || interactiveShape;
      layer.add(layerShape);
      layerShape.on("mouseover", () => doHover.show());
      layerShape.on("mouseout", () => doHover.hide());

      // give away full control over interactions
      return { interactiveShape: layerShape, tooltipTargetShape: interactiveShape, doInteract: doHover };
    }

    function text(text: string, _outside = false, fontSize = 10, shouldRender = (_: number) => true) {
      const textHeight = 12;
      // One draws text
      const textShape = new Konva.Text({
        fontSize,
        fontFamily: "Roboto Flex Mono",
        fill: "#242526",
      });
      textShape.text(text);
      const textWidth = textShape.width();

      if (shouldRender(textWidth)) {
        textShape.x(Math.max(begin, visibleStart + 2));
        const textY = Math.ceil(y - textHeight);
        textShape.y(textY);
        textShape.transformsEnabled("position");
        layer.add(textShape);

        textInteractiveShape = new Konva.Rect({
          x: begin,
          y: textY,
          width: Math.max(textWidth, width),
          height: height + textHeight,
        });
      }
    }

    return {
      text,
      interactive,
      highlight,
    };
  };

  const renderArrow = ({
    start,
    stop,
    direction,
    fill,
    hasStroke,
    y,
    opacity,
    height,
    inclusiveStartPosition,
  }: IRenderArrowShapeParams): GenomeViewerDefaultShape => {
    let [begin, end, width] = normalize(inclusiveStartPosition ? start - 1 : start, stop, Math.ceil);
    const arrowWidth = Math.ceil(height / 2);
    const doStroke = hasStroke && height > 3;

    // force a shape to be visible (min 1px)
    if (width < 1) end = begin + 1;

    const stroke = darken(0.35, saturate(0.1, fill));
    const dark = darken(0.15, fill);

    function getArrowPoints() {
      // don't draw as arrow of the shape is too narrow
      if (width < arrowWidth + 2) direction = ArrowDirection.None;

      const moveStroke = doStroke ? 0.5 : 0;
      const points = [
        begin + (direction === ArrowDirection.Left ? arrowWidth : 0) + moveStroke,
        y + moveStroke,
        end - (direction === ArrowDirection.Right ? arrowWidth : 0) - moveStroke,
        y + moveStroke,
        end - moveStroke,
        y + height / 2,
        end - (direction === ArrowDirection.Right ? arrowWidth : 0) - moveStroke,
        y + height - moveStroke,
        begin + (direction === ArrowDirection.Left ? arrowWidth : 0) + moveStroke,
        y + height - moveStroke,
        begin + moveStroke,
        y + height / 2,
      ];
      return points;
    }

    // Arrow Shape
    const shape = new Konva.Line({
      points: getArrowPoints(),
      fill: doStroke ? fill : dark,
      opacity,
      closed: true,
      strokeEnabled: doStroke,
      stroke,
      strokeWidth: doStroke ? 1 : 0,
      fillAfterStrokeEnabled: doStroke,
    });
    shape.transformsEnabled("position");
    layer.add(shape);

    const highlight = getHighlightFn(layer, Math.max(begin, visibleStart), Math.min(end, visibleEnd), y);

    function getInteractionOverlay(): Konva.Shape {
      return new Konva.Line({
        points: getArrowPoints(),
        closed: true,
      });
    }

    let textInteractiveShape: Konva.Rect;
    function interactive(): GenomeViewerShapeInteractivity {
      const interactionOverlay = getInteractionOverlay();
      const tooltipStart = Math.max(begin, visibleStart);
      const tooltipStop = Math.min(end, visibleEnd);
      const interactiveShape = new Konva.Rect({ x: tooltipStart, y, width: tooltipStop - tooltipStart, height });
      interactiveShape.transformsEnabled("position");
      const doHover = renderInteraction(interactionOverlay, InteractiveState.Hover);

      const layerShape = textInteractiveShape || interactiveShape;
      layer.add(layerShape);
      layerShape.on("mouseover", () => doHover.show());
      layerShape.on("mouseout", () => doHover.hide());

      // give away full control over interactions
      return { interactiveShape: layerShape, tooltipTargetShape: interactiveShape, doInteract: doHover };
    }

    function text(text: string, outside = false, fontSize = 9, shouldRender = (_: number) => true) {
      const textStart = Math.max(begin, visibleStart);
      const textStop = Math.min(end, visibleEnd);
      const textWidth = textStop - textStart;

      if (!(height < 9 || (outside === false && textWidth < 7))) {
        // One draws text
        const textShape = new Konva.Text({
          fontSize,
          fontFamily: "Roboto Flex Mono",
          fill: outside === true ? "#242526" : readableColor(hasStroke ? fill : stroke, "#242526", "#ffffff"),
        });
        textShape.text(text);

        if (shouldRender(textShape.width())) {
          let textX = textStart + (textWidth - textShape.width()) / 2; // => ArrowDirection.None

          if (outside === true) {
            textX = begin - textShape.width() - 2;
          } else if (direction === ArrowDirection.Left) {
            textX = textStart + (textWidth - textShape.width() - arrowWidth) / 2 + (arrowWidth - 1);
          } else if (direction === ArrowDirection.Right) {
            textX = textStart + (textWidth - textShape.width() - arrowWidth) / 2;
          }

          textShape.x(textX);
          textShape.y(y + Math.floor((height - fontSize) / 2) + 1);

          textShape.transformsEnabled("position");
          layer.add(textShape);

          textInteractiveShape = new Konva.Rect({
            x: outside === true ? textX : begin,
            y,
            width: outside ? width + textShape.width() + 2 : width,
            height,
          });
        }
      }
    }

    return {
      text,
      interactive,
      highlight,
    };
  };

  const renderLine = ({
    start,
    stop,
    fill,
    y,
    opacity,
    height,
    thickness,
    inclusiveStartPosition,
  }: IRenderLineShapeParams): GenomeViewerDefaultShape => {
    const [begin, end, width] = normalize(inclusiveStartPosition ? start - 1 : start, stop, Math.ceil);
    const thick = Math.min(height, thickness);
    const yLine = Math.ceil(y + height / 2 - thick / 2);
    const points = [
      Math.ceil(begin),
      yLine,
      Math.ceil(end),
      yLine,
      Math.ceil(end),
      yLine + thick,
      Math.ceil(begin),
      yLine + thick,
    ];

    if (width > 1) {
      const shape = new Konva.Line({
        points,
        fill,
        opacity,
        closed: true,
        listening: false,
      });

      shape.transformsEnabled("position");
      layer.add(shape);

      if (interactionLayer != undefined) {
        return {
          interactive(): GenomeViewerShapeInteractivity {
            // Interactive Shape
            const tooltipStart = Math.max(begin, visibleStart);
            const tooltipStop = Math.min(end, visibleEnd);
            const interactiveShape = new Konva.Rect({ x: tooltipStart, y, width: tooltipStop - tooltipStart, height });
            interactiveShape.transformsEnabled("position");
            layer.add(interactiveShape);
            const doHover = renderInteraction(interactiveShape, InteractiveState.Hover);
            interactiveShape.on("mouseover", () => doHover.show());
            interactiveShape.on("mouseout", () => doHover.hide());

            // give away full control over interactions
            return {
              interactiveShape: interactiveShape,
              tooltipTargetShape: interactiveShape,
              doInteract: doHover,
            };
          },
        };
      }

      // Nothing to do if not interactive
      return {};
    }
  };

  const renderInsertion = ({ start, fill, y, height }: IRenderInsertionsShapeParams): GenomeViewerDefaultShape => {
    // -1px and +1px of current position to be 3px large
    const [x] = normalize(start, start + 1, Math.ceil);

    const points = [
      x - 2,
      y,
      x + 1,
      y,
      x + 1,
      y + 2,
      x,
      y + 2,
      x,
      y + height - 2,
      x + 1,
      y + height - 2,
      x + 1,
      y + height,
      x - 2,
      y + height,
      x - 2,
      y + height - 2,
      x - 1,
      y + height - 2,
      x - 1,
      y + 2,
      x - 2,
      y + 2,
    ];

    const shape = new Konva.Line({
      points,
      fill,
      closed: true,
      stroke: "#ffffff",
      strokeWidth: 2,
      fillAfterStrokeEnabled: true,
    });
    shape.transformsEnabled("position");
    layer.add(shape);

    if (interactionLayer != undefined) {
      return {
        interactive(): GenomeViewerShapeInteractivity {
          const interactiveShape = new Konva.Rect({
            x: x - 4,
            y,
            width: 7,
            height,
          });
          interactiveShape.transformsEnabled("position");
          layer.add(interactiveShape);
          const doHover = renderInteraction(interactiveShape, InteractiveState.Hover);
          interactiveShape.on("mouseover", () => doHover.show());
          interactiveShape.on("mouseout", () => doHover.hide());

          // give away full control over interactions
          return { interactiveShape: interactiveShape, tooltipTargetShape: interactiveShape, doInteract: doHover };
        },
      };
    }

    return {};
  };

  return { renderMapping, renderArrow, renderLine, renderInsertion };
}

enum InteractiveState {
  Selected,
  Hover,
}

export interface IFeatureInteraction {
  show: (lighter?: boolean) => void;
  hide: () => void;
}
function getInteractiveRenderer(layer: Konva.Layer) {
  return function render(shape: Konva.Shape, state: InteractiveState): IFeatureInteraction {
    if (layer == undefined) return;

    if (state === InteractiveState.Hover) {
      let shapeInteractionOverlay: Konva.Shape;

      return {
        show(lighter?: boolean) {
          shapeInteractionOverlay = shape.clone({
            fill: null,
            stroke: lighter ? "#bd6846" : "#669df6",
            strokeWidth: lighter ? 1 : 1.5,
            shadowForStrokeEnabled: true,
            fillAfterStrokeEnabled: false,
          } as ShapeConfig);
          layer.add(shapeInteractionOverlay);
          layer.batchDraw();
        },
        hide() {
          shapeInteractionOverlay.remove();
          layer.batchDraw();
        },
      };
    }
  };
}

interface IDrawMessageOnTrackProps {
  resetHeight?: boolean;
  withOverlay?: boolean;
  stickToTop?: boolean;
  stickToBottom?: boolean;
  interactiveStyle?: boolean;
}
function drawMessageOnTrack(layer: Konva.Layer, draw = true) {
  /**
   * @return number: The rendered text height
   */
  return function (text: string, params: IDrawMessageOnTrackProps): { shape: Konva.Shape; textTopMargin: number } {
    const resetHeight = params.resetHeight || false;
    const withOverlay = params.withOverlay || false;
    const stickToTop = params.stickToTop || false;
    const stickToBottom = params.stickToBottom || false;
    const interactiveStyle = params.interactiveStyle || false;

    const textBackgroundPadding = Math.min(8, CANVAS_TRACK_MARGIN);
    const layerText = new Konva.Text({
      fontSize: 12,
      align: "center",
      fontFamily: "Roboto Flex, sans-serif",
      fill: interactiveStyle ? "#2988cc" : withOverlay ? "#ffffff" : "#797c80",
      listening: false,
    });

    const stage = layer.getStage();
    let textHeight = 0;

    if (stage == undefined) {
      return;
    }

    layerText.text(text);

    const textWidth = layerText.width();
    textHeight = layerText.height();

    if (resetHeight === true) stage.height(textHeight + CANVAS_TRACK_MARGIN * 2);

    const x = Math.ceil((stage.width() - textWidth) / 2); // left of the text shape
    let y = Math.ceil((stage.height() - textHeight) / 2) - CANVAS_TRACK_MARGIN;

    if (stickToTop === true) {
      // The text layer already has been moved down by CANVAS_TRACK_MARGIN
      y = 0;
    } else if (stickToBottom === true) {
      // Show the message under the rest, meaning that the total track height accounts for this message
      y = Math.ceil(stage.height() - textHeight) - CANVAS_TRACK_MARGIN * 2;
    }

    layerText.x(x);
    layerText.y(y);

    const background = new Konva.Rect({
      x: x - textBackgroundPadding,
      y: y - Math.ceil(textBackgroundPadding / 2),
      width: Math.ceil(textWidth + textBackgroundPadding * 2),
      height: textHeight + textBackgroundPadding,
      fill: withOverlay ? "#797C80" : "white",
      cornerRadius: 2,
      opacity: 0.9,
    });

    if (interactiveStyle) {
      background.on("mouseenter", () => {
        stage.container().style.cursor = "pointer";
      });
      background.on("mouseleave", () => {
        stage.container().style.cursor = "default";
      });
    }

    if (withOverlay === true) {
      const overlay = new Konva.Rect({
        x: 0,
        y: 0,
        width: layer.width(),
        height: layer.height(),
        fill: "#ffffff",
        opacity: 0.45,
        listening: false,
      });

      layer.add(overlay);
    }

    layer.add(background);
    layer.add(layerText);

    if (draw === true) {
      layer.draw();
    }

    return { shape: background, textTopMargin: textHeight + textBackgroundPadding };
  };
}

const renderAminoAcidSequence = (
  feature: IPileupFeature,
  pileupParams: IPileupParams,
  startY: number,
  selected: boolean,
  renderArrow: ReturnType<typeof getShapeRenderer>["renderArrow"], // This has been configured with normalize() already
) => {
  const origFeature: Feature = feature.feature;
  const strand: string = origFeature.region.strand;
  const origSequence: string = origFeature.sequence;
  // Remove the trailing asterisk if it exists. We show an empty box there instead.
  let sequence: string = origSequence.endsWith("*") ? origSequence.slice(0, -1) : origSequence;

  if (strand === STRAND_TYPE.MINUS) {
    // Add a trailing empty box as representative of the stop codon if working on the minus strand
    // (we need it because we access the string from the first position, starting with the stop codon).
    sequence = " " + sequence.split("").reverse().join("");
  }

  let basePosInProtein = 0;

  // Loop through all exons of the protein
  for (let i = 0; i < feature.mappings.length; i++) {
    let basePosInMapping = 0;

    // the pixel mapping
    const mapping: IPileupFeatureMapping = feature.mappings[i];

    // the original contig position of the exon (in base coordinates)
    const origRange: Range = origFeature.mappings[i];

    // number of bases contained in the current exon
    const exonLengthInBases: number = origRange.stop - origRange.start + 1;

    // pixel width of a base
    const baseWidth: number = mapping.width / exonLengthInBases;

    // loop through all bases of the exon and decide if a letter or a box should be drawn
    for (let be = 0; be < exonLengthInBases; be++) {
      const xPos = Math.round(mapping.x + be * baseWidth); // left border of the amino acid box

      // Limit the rendering to a useful range to speed up the display
      if (xPos > -100 && xPos < pileupParams.canvasWidth + 100) {
        // Find out the amino acid encoded by the current exon base
        const aa = sequence.substr(Math.floor(basePosInProtein / 3), 1).trim();

        // The box is drawn with the first base, either of the exon, or of the codon
        const isBoxPosition: boolean = be === 0 || basePosInProtein % 3 === 0;

        if (aa && isBoxPosition) {
          // The box may be three bases long, or it is shortened because the exon ends before the codon ends
          const boxWidthInBases: number = Math.min(3, exonLengthInBases - be);
          const start = mapping.start + basePosInMapping - 1; // => inclusiveStartPosition
          let color = SEQ_RESIDUE_AA_COLORS[aa.toLowerCase()] ?? "#ffffff";

          if (selected === true) {
            // Add 33% of blue to the current color, so effect will be like gene selection
            color = mix(0.66, color, "#42affa");
          }

          renderArrow({
            start,
            stop: start + boxWidthInBases,
            direction: ArrowDirection.None,
            fill: color,
            hasStroke: false,
            height: pileupParams.featureHeight,
            y: startY,
            inclusiveStartPosition: false, // We MUST do that because stop depends of start
          }).text(aa); // pileupParams.showSeqLetters is useless as drawing text or not is automatic
        }
      }

      // we proceed one base within the full coding sequence
      basePosInProtein += 1;
      basePosInMapping += 1;
    }
  }
};

/// --- Export --- ///
export {
  ArrowDirection,
  drawMessageOnTrack,
  getInteractiveRenderer,
  getShapeRenderer,
  InteractiveState,
  renderAminoAcidSequence,
};
